import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { store } from '../../../../state/configureStore';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { getCursor } from '../../../../state/selectors/ui/tables';
import type { SegmentTransferTarget } from '../../../../types/surveillanceRoute';

/** Filas por página: las de las demás ventanas de tabla. */
export const TARGET_PAGE_SIZE = 10;

/** Los filtros como los entiende el listado de recorridos. */
export type TargetFilter = {
  name?: string;
  fromDate?: string;
  toDate?: string;
  /** «Fecha final» (`registerEndDate`); el servidor admite un solo extremo. */
  fromEndDate?: string;
  toEndDate?: string;
  indexExpedition?: number;
  indexType?: number;
  sortField?: string;
  orderBy?: string;
};

export type TargetPage = {
  targets: SegmentTransferTarget[];
  totalCount: number;
};

export type FetchTargetPage = (args: {
  filter: TargetFilter;
  skip: number;
  take: number;
}) => Promise<TargetPage>;

type Cursor = {
  routeName?: string;
  fromDate?: string;
  toDate?: string;
  fromEndDate?: string;
  toEndDate?: string;
  indexExpedition?: number | null;
  indexType?: number | null;
  sortField?: string;
  orderBy?: string;
};

const filterFromCursor = (cursor: Cursor | undefined): TargetFilter => ({
  name: cursor?.routeName || undefined,
  // El listado solo filtra por fechas con las dos puestas.
  ...(cursor?.fromDate &&
    cursor?.toDate && { fromDate: cursor.fromDate, toDate: cursor.toDate }),
  fromEndDate: cursor?.fromEndDate || undefined,
  toEndDate: cursor?.toEndDate || undefined,
  indexExpedition: cursor?.indexExpedition ?? undefined,
  indexType: cursor?.indexType ?? undefined,
  sortField: cursor?.sortField || undefined,
  orderBy: cursor?.orderBy || undefined,
});

/**
 * La tabla de destinos de «Transferir tramo» y «Mover tramos» con los filtros,
 * el orden y las páginas de las demás ventanas de transferir (revisión del
 * 22-oct). La tabla compartida guarda los filtros en su cursor de Redux y
 * avisa con `onReFetchData`; aquí se leen en ese momento, se pide la página al
 * servidor y se lleva la cuenta de páginas.
 *
 * `fetchPage` a `null` deja la tabla vacía (en «Mover tramos», sin rango).
 * Una respuesta vieja nunca pisa la de la última petición.
 *
 * 23-oct (el usuario): se abre SIN filtros, como las demás ventanas de
 * transferir (antes, con las fechas del tramo ±1 día), y mientras pide una
 * página la tabla enseña su «cargando»: la tabla compartida solo lo pinta con
 * el flag de Redux de su `tableId`, no con el `loading` de sus datos.
 *
 * 27-oct (el usuario: «no cachea y tarda un montón en cambiar de página»):
 * cada página ya vista se guarda mientras la ventana está abierta (por filtro
 * y número de página), y volver a ella no pide nada. Al cerrar se olvida, y
 * al cambiar lo que se mueve (otro rango en «Mover») también.
 *
 * 29-oct (el usuario: «si borro el filtro salta el cargando cuando debería
 * estar cacheado»): buscar, borrar filtros y ordenar también reutilizan lo ya
 * visto, como las demás tablas; solo recargar (↻) pide lo de ahora. Y un
 * filtro nuevo se pide sin «cargando»: siguen las filas de antes hasta que
 * llegan las nuevas, como en la tabla principal de órdenes de trabajo. Abrir,
 * las flechas de página y ↻ sí lo enseñan.
 */
export function useTargetPage(
  tableId: string,
  fetchPage: FetchTargetPage | null
) {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<TargetPage>({ targets: [], totalCount: 0 });
  const [isLoading, setIsLoading] = useState(false);
  // El error tal cual: «Mover tramos» distingue sin red, su motivo y el resto.
  const [loadError, setLoadError] = useState<unknown>(null);
  const [ready, setReady] = useState(false);
  const requestRef = useRef(0);
  const cacheRef = useRef(new Map<string, TargetPage>());

  // Al abrir, filtros limpios: los de otra apertura eran de otro tramo. El
  // cursor solo borra un campo con `undefined` explícito.
  useEffect(() => {
    dispatch(
      setCursor(tableId, {
        cursor: '',
        take: TARGET_PAGE_SIZE,
        routeName: undefined,
        indexExpedition: undefined,
        indexType: undefined,
        sortField: undefined,
        orderBy: 'desc',
        fromDate: undefined,
        toDate: undefined,
        fromEndDate: undefined,
        toEndDate: undefined,
      })
    );
    setReady(true);
    // Al cerrar, que la tabla no se quede «cargando» para la próxima vez.
    return () => {
      dispatch(loadingData(tableId, false));
    };
  }, [dispatch, tableId]);

  // El «cargando» de la tabla compartida sigue al de la página.
  const setLoading = useCallback(
    (value: boolean) => {
      setIsLoading(value);
      dispatch(loadingData(tableId, value));
    },
    [dispatch, tableId]
  );

  const load = useCallback(
    async (targetPage: number, { quiet = false }: { quiet?: boolean } = {}) => {
      const id = ++requestRef.current;
      if (!fetchPage) {
        setData({ targets: [], totalCount: 0 });
        setPage(1);
        setLoading(false);
        setLoadError(null);
        return;
      }
      const cursor = getCursor(tableId)(store.getState()) as Cursor;
      const filter = filterFromCursor(cursor);
      const key = `${JSON.stringify(filter)}|${targetPage}`;
      const cached = cacheRef.current.get(key);
      if (cached) {
        setData(cached);
        setPage(targetPage);
        setLoading(false);
        setLoadError(null);
        return;
      }
      if (!quiet) setLoading(true);
      setLoadError(null);
      try {
        const result = await fetchPage({
          filter,
          skip: (targetPage - 1) * TARGET_PAGE_SIZE,
          take: TARGET_PAGE_SIZE,
        });
        if (id !== requestRef.current) return;
        cacheRef.current.set(key, result);
        setData(result);
        setPage(targetPage);
      } catch (error) {
        if (id !== requestRef.current) return;
        setData({ targets: [], totalCount: 0 });
        setLoadError(error ?? new Error('load failed'));
      } finally {
        if (id === requestRef.current) setLoading(false);
      }
    },
    [fetchPage, setLoading, tableId]
  );

  // Lo de antes era de otro rango (o de otro tramo): se empieza de cero.
  useEffect(() => {
    cacheRef.current.clear();
    if (ready) void load(1);
  }, [ready, load]);

  /** Pedir de nuevo, sin lo guardado. */
  const fresh = useCallback(
    (targetPage: number) => {
      cacheRef.current.clear();
      return load(targetPage);
    },
    [load]
  );

  const pageCount = Math.max(1, Math.ceil(data.totalCount / TARGET_PAGE_SIZE));

  return {
    targets: data.targets,
    totalCount: data.totalCount,
    page,
    pageCount,
    isLoading,
    loadFailed: loadError !== null,
    loadError,
    /** La tabla aplicó filtros u orden: página 1, de lo guardado si ya se vio. */
    onReFetchData: useCallback(() => load(1, { quiet: true }), [load]),
    /** Recargar (↻): página 1, pedida de nuevo. */
    onReloadData: useCallback(() => fresh(1), [fresh]),
    /** Repetir la página actual (tras un error al guardar, p. ej.). */
    reload: useCallback(() => fresh(page), [fresh, page]),
    nextPage: useCallback(() => {
      if (page < pageCount) void load(page + 1);
    }, [load, page, pageCount]),
    previousPage: useCallback(() => {
      if (page > 1) void load(page - 1);
    }, [load, page]),
  };
}
